import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, Shirt } from 'lucide-react';
import { toast } from 'sonner';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dihrlt2x7';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'ml_default';

/**
 * Compresses an image using Canvas API before uploading.
 * Returns a Blob with max 1500px width, JPEG quality 0.85.
 */
const compressImage = (file) => {
  return new Promise((resolve) => {
    const MAX_WIDTH = 1500;
    const QUALITY = 0.85;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, MAX_WIDTH / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

export const ImageUploader = ({
  images = [],
  onImagesChange,
  maxImages = 10,
  folder = 'davocado/products',
  tryonImage = null,
  onTryonImageChange = null,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', folder);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleFiles = async (files) => {
    const validFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} no es una imagen válida`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} es muy grande (máx 10MB)`);
        return false;
      }
      return true;
    });

    if (images.length + validFiles.length > maxImages) {
      toast.error(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }

    if (validFiles.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of validFiles) {
      try {
        const compressed = await compressImage(file);
        const url = await uploadToCloudinary(compressed);
        uploadedUrls.push(url);
      } catch (error) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      onImagesChange([...images, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} imagen(es) subida(s)`);
    }

    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const removeImage = (index) => {
    onImagesChange(images.filter((_, i) => i !== index));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed rounded-none p-8 text-center cursor-pointer transition-colors
          ${dragActive 
            ? 'border-gold bg-gold/10' 
            : 'border-white/20 hover:border-gold/50 hover:bg-white/5'
          }
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-gold animate-spin mb-3" />
            <p className="text-white/60">Subiendo imágenes...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="w-10 h-10 text-white/40 mb-3" />
            <p className="text-white font-medium mb-1">
              Arrastra imágenes aquí o haz clic para seleccionar
            </p>
            <p className="text-white/40 text-sm">
              PNG, JPG, WEBP hasta 10MB (máx {maxImages} imágenes)
            </p>
          </div>
        )}
      </div>

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {images.map((url, index) => (
            <div
              key={index}
              className="relative aspect-square bg-white/5 group"
            >
              <img
                src={url}
                alt={`Imagen ${index + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => moveImage(index, index - 1)}
                    className="p-1 bg-white/20 text-white text-xs hover:bg-white/30"
                    title="Mover izquierda"
                  >
                    ←
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="p-1 bg-red-500/80 text-white hover:bg-red-500"
                  title="Eliminar"
                >
                  <X className="w-4 h-4" />
                </button>
                {index < images.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveImage(index, index + 1)}
                    className="p-1 bg-white/20 text-white text-xs hover:bg-white/30"
                    title="Mover derecha"
                  >
                    →
                  </button>
                )}
              </div>

              {onTryonImageChange && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTryonImageChange(tryonImage === url ? '' : url);
                  }}
                  className={`absolute bottom-1 right-1 w-6 h-6 flex items-center justify-center rounded-full transition-all ${
                    tryonImage === url
                      ? 'bg-cyan-500 text-white'
                      : 'bg-black/50 text-white/50 hover:bg-black/70'
                  }`}
                  title={tryonImage === url ? 'Quitar del probador' : 'Usar en probador virtual'}
                >
                  <Shirt className="w-3 h-3" />
                </button>
              )}

              {index === 0 && (
                <span className="absolute top-1 left-1 bg-gold text-black text-xs px-1 font-medium">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Single image uploader for categories
export const SingleImageUploader = ({ 
  imageUrl, 
  onImageChange,
  folder = 'davocado/categories'
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', folder);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: 'POST', body: formData }
    );
    
    if (!response.ok) throw new Error('Upload failed');
    return (await response.json()).secure_url;
  };

  const handleFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const url = await uploadToCloudinary(compressed);
      onImageChange(url);
      toast.success('Imagen subida');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative aspect-video border-2 border-dashed rounded-none cursor-pointer transition-colors overflow-hidden
          ${imageUrl ? 'border-gold/50' : 'border-white/20 hover:border-gold/50'}
          ${uploading ? 'pointer-events-none' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          className="hidden"
        />

        {uploading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : imageUrl ? (
          <>
            <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-sm">Clic para cambiar</span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <ImageIcon className="w-10 h-10 text-white/30 mb-2" />
            <span className="text-white/40 text-sm">Clic para subir imagen</span>
          </div>
        )}
      </div>

      {imageUrl && (
        <button
          type="button"
          onClick={() => onImageChange('')}
          className="text-red-500 text-sm hover:text-red-400"
        >
          Eliminar imagen
        </button>
      )}
    </div>
  );
};
